import { TokenData } from '../../models/auth/TokenData';
import { client } from '../apiClient';
import { REACT_APP_CLIENT_ID } from '../../utils/config';

export const sendMagicLink = async (email: string, fingerprint?: string, language?: string): Promise<void> => {
  await client(true, false).post('/auth/magic-link/send', { email, fingerprint, language });
};

export const authenticateWithMagicLink = async (token: string, fingerprint?: string): Promise<TokenData> => {
  const response = await client(true, false).post<TokenData>('/auth/magic-link/authenticate', {
    token,
    client_id: REACT_APP_CLIENT_ID,
    fingerprint,
  });
  return response.data;
};

export const authenticateWithGoogle = async (
  credential: string,
  fingerprint?: string,
  language?: string
): Promise<TokenData> => {
  const response = await client(true, false).post<TokenData>('/auth/google', {
    credential,
    client_id: REACT_APP_CLIENT_ID,
    fingerprint,
    language,
  });
  return response.data;
};

// Reverse SSO landing: exchange a one-time code (minted by ChatBeat, handed off from masterclass)
// for a real session. Public call — the user isn't authenticated yet, the code is the proof.
export const authenticateWithSsoCode = async (code: string): Promise<TokenData> => {
  const response = await client(true, false).post<TokenData>('/sso/masterclass/exchange', { code });
  return response.data;
};

export const autoReactivate = async (token: string): Promise<TokenData> => {
  const response = await client(true, false).get<TokenData>('/auth/auto-reactivation', {
    params: { token, client_id: REACT_APP_CLIENT_ID },
  });
  return response.data;
};

export const TOKEN_KEY = 'token';

export const saveTokenData = (token: TokenData): void => {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
};

export const updateTokenData = (firstName: string, lastName: string): void => {
  const tokenData = getTokenData();

  if (tokenData) {
    tokenData.user = { ...tokenData.user, firstName, lastName };
    saveTokenData(tokenData);
  }
};

// Uszkodzony wpis (np. pusta odpowiedź `/auth/refresh` zapisana jako `""`) wywracał tu całą aplikację
// białym ekranem — przypisanie property na prymitywie rzuca TypeError. Traktujemy go jak brak tokenu.
export const getTokenData = (): TokenData | null => {
  const tokenString = localStorage.getItem(TOKEN_KEY);

  if (!tokenString) {
    return null;
  }

  let tokenData: unknown;
  try {
    tokenData = JSON.parse(tokenString);
  } catch {
    tokenData = null;
  }

  if (!isTokenData(tokenData)) {
    removeTokenData();
    return null;
  }

  tokenData.accessTokenExpiresAt = new Date(tokenData.accessTokenExpiresAt);
  return tokenData;
};

const isTokenData = (value: unknown): value is TokenData =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as TokenData).accessToken === 'string' &&
  typeof (value as TokenData).refreshToken === 'string';

export const removeTokenData = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

export const isTokenValid = (tokenData: TokenData): boolean =>
  tokenData.accessTokenExpiresAt.getTime() > Date.now();

export const refreshAccessToken = async (): Promise<TokenData> => {
  try {
    const tokenDataFromStorage = getTokenData();
    if (!tokenDataFromStorage) {
      throw Error('Incorrect refresh token');
    }

    const data = new URLSearchParams();
    data.append('grant_type', 'refresh_token');
    data.append('client_id', REACT_APP_CLIENT_ID);
    data.append('refresh_token', tokenDataFromStorage.refreshToken);

    const response = await client(false, false).post('/auth/refresh', data.toString());

    // Odpowiedź bez ciała daje w axiosie `''`; zapisane jako token psuje storage na kolejne wejścia.
    if (!isTokenData(response.data)) {
      throw Error('Malformed refresh response');
    }

    return response.data;
  } catch (error) {
    throw Error('Unable to refresh token');
  }
};
